import { GameServices } from "game/GameServices";
import { SoundSystem } from "./SoundSystem";
import { Signal, SignalBinding } from "type-signals";
import { Tween } from "@westpierstudio/luma/createjs";
import { BonusSymbolID, CoreSymbolID, ReelSetSymbols, ReelSymbols, Win } from "servertypes/SharedTypes";
import { Game } from "@westpierstudio/luma/game";
import { GameEvent } from "servertypes/ServerEvents";
import { devLog, randomBool, range } from "utils/Misc";
import { Howl } from "howler";
import { RandomSequence } from "utils/RandomSequence";
import { WinlinesModel } from "components/winlines/WinlinesModel";

type SoundSettings = {
    counts: SoundSettingCounts,
    thresholds: SoundSettingThresholds,
    bonusBellRetriggerLength: number,
    bonusRollupFadeInTime: number,
}

type SoundSettingCounts = {
    crossbowWin: number,
    retriggerValues: number[],
    youveWonValues: number[],
    crossbowIncrement: number[]
}

type SoundSettingThresholds = {
    crossbowWin: number;
    rollupSmall: number;
    rollupMed: number;
    rollupLarge: number;
    summaryLow: number;
    summaryMed: number;
    symbolWinSound: number;
    wildWinSound: number;
    consolation: number;
}

type TweenObjects = {
    bonusBellRetrigger: Record<string, unknown>
}

class SoundSignals {
    bonusBellRetriggerComplete = new Signal();
}

enum RollupSound {
    None = 0,
    Small = 1,
    Medium = 2,
    Large = 3,
    Big = 4,
    Bonus = 5,
}

export class SoundPlayer {
    private readonly system: SoundSystem;
    private readonly settings: SoundSettings;
    private readonly tweenObjects: TweenObjects;
    private readonly crossbowBigwin: RandomSequence<number>;
    private currentRollup: RollupSound = RollupSound.None;
    private bonusRollupHowl?: Howl = undefined;
    private startBonusMusicSignalBinding?: SignalBinding;

    signals = new SoundSignals();


    public get enableLogging(): boolean {
        return this.system.enableLogging;
    }


    public set enableLogging(v: boolean) {
        this.system.enableLogging = v;
    }



    constructor(protected readonly services: GameServices) {

        this.system = new SoundSystem(services);
        this.settings = this.services.resources.getSetting(`sound-settings`);

        this.tweenObjects = { bonusBellRetrigger: {} };

        this.crossbowBigwin = new RandomSequence(range(1, this.settings.counts.crossbowWin));
    }

    reelStop(): void {
        this.system.play(`reelStop`);
    }

    anticipate(): void {
        this.system.play(`anticipation`);
    }

    skipAnticipate() {
        this.system.stop(`anticipation`);
    }

    playBonusMusic() {
        this.system.play(`bonusMusic`, { loop: true });
        this.bonusRollupHowl = this.system.play(`bonusRollup`, { loop: true, vol: 0 });
        this.bonusRollupHowl?.volume(0);
    }

    startBonusMusic(): void {
        const { audio } = this.services;
        if (audio.soundReady) {
            this.playBonusMusic();
        }
        else {
            this.startBonusMusicSignalBinding = audio.onSoundsLoaded.once(this.playBonusMusic, this);
        }
    }

    stopBonusMusic() {
        this.system.stop(`bonusMusic`);
        this.system.stop(`bonusRollup`);
        this.bonusRollupHowl?.stop();
        this.bonusRollupHowl = undefined;
        this.system.play(`bonusMusicEnd`);
        if (this.startBonusMusicSignalBinding) {
            this.services.audio.onSoundsLoaded.detach(this.startBonusMusicSignalBinding);
        }
    }

    fadeBonusMusic(to: number, duration: number = 1000) {
        const music = this.system.getSoundData(`bonusMusic`);
        if (music.sound) {
            music.sound.fade(music.volume, to, duration);
        }
    }

    protected startBonusRollup(): void {
        if (this.bonusRollupHowl) {
            this.bonusRollupHowl.fade(0, 1, this.settings.bonusRollupFadeInTime);
        }
    }

    protected stopBonusRollup() {
        if (this.bonusRollupHowl) {
            this.bonusRollupHowl.fade(1, 0, 0);//https://github.com/goldfire/howler.js/issues/1543
            this.bonusRollupHowl.volume(0);
            this.bonusRollupHowl.volume(0);//set volume twice workaround? ref: https://github.com/goldfire/howler.js/issues/1587
            this.system.play(`bonusRollupTerm`);
        }
    }

    startRollup(moneyWon: number, totalBet: number, isFreeGame = false) {
        if (moneyWon <= 0) return;
        const multiplierOfBetWon = moneyWon / totalBet;
        if (this.currentRollup !== RollupSound.None) {
            this.stopCurrentRollup();
        }
        if (multiplierOfBetWon <= this.settings.thresholds.consolation) {
            this.consolation();
        }
        else if (isFreeGame) {
            this.currentRollup = RollupSound.Bonus;
            this.startBonusRollup();
        }
        else if (multiplierOfBetWon < this.settings.thresholds.rollupSmall) {
            this.currentRollup = RollupSound.Small;
            this.system.play(`rollupSmall`, { loop: true });
        }
        else if (isFreeGame || multiplierOfBetWon < this.settings.thresholds.rollupMed) {
            this.currentRollup = RollupSound.Medium;
            this.system.play(`rollupMed`, { loop: true });
        }
        else if (multiplierOfBetWon < this.settings.thresholds.rollupLarge) {
            this.currentRollup = RollupSound.Large;
            this.system.play(`rollupLarge`, { loop: true });
        }
        else {
            this.currentRollup = RollupSound.Big;
            this.system.play(`rollupBig`, { loop: true });
        }
    }

    stopCurrentRollup(): void {
        const rollup = this.currentRollup;
        switch (rollup) {
            case RollupSound.Small:
                this.system.stop(`rollupSmall`);
                this.system.play(`rollupSmallEnd`);
                break;
            case RollupSound.Medium:
                this.system.stop(`rollupMed`);
                this.system.play(`rollupMedEnd`);
                break;
            case RollupSound.Large:
                this.system.stop(`rollupLarge`);
                this.system.play(`rollupLargeEnd`);
                break;
            case RollupSound.Big:
                this.system.stop(`rollupBig`);
                this.system.play(`rollupBigEnd`);
                break;
            case RollupSound.Bonus:
                this.stopBonusRollup();
            default:
                break;
        }
        this.currentRollup = RollupSound.None;
    }

    bonusHit(index: number): void {
        this.system.play(`bonusHit${index}`);
    }

    transition(): void {
        this.system.play(`transition`);
    }

    stopTransition(): void {
        this.system.stop(`transition`);
    }

    robinhood(): void {
        this.system.play(`robinhood`);
    }

    youveWon(games: number): void {
        if (this.settings.counts.youveWonValues.includes(games)) {
            this.system.play(`youveWon${games}`);
        }
        else {
            devLog(`Failed to play sound: YouveWon ${games} FreeGames`);
        }
    }

    retrigger(games: number): void {
        if (this.settings.counts.retriggerValues.includes(games)) {
            this.system.play(`retrigger${games}`);
        }
        else {
            //need to ask the backend to see if the additional retrigger games are clamped if the games exceed the max
            this.system.play(`retriggerX`);
        }
    }

    bonusTotal(bonusMultiplierOfBetWon: number): void {
        if (bonusMultiplierOfBetWon < this.settings.thresholds.summaryLow) {
            this.system.play(`summaryLow` + (randomBool() ? `A` : `B`));
        }
        else if (bonusMultiplierOfBetWon < this.settings.thresholds.summaryMed) {//Content key is not clear about summary sound between 40x and 50x
            this.system.play(`summaryMed` + (randomBool() ? `A` : `B`));
        }
        else {
            this.system.play(`summaryHigh` + (randomBool() ? `A` : `B`));
        }
    }

    winlineSound(winlines: WinlinesModel, playerBet: number) {
        const totalMoneyWon = winlines.winList.reduce((prev, curr) => curr.moneyWon + prev, 0);
        const { usesWild, symbol } = winlines.winList[0];
        const multiplierOfBetWon = totalMoneyWon / playerBet;
        if (usesWild && multiplierOfBetWon >= this.settings.thresholds.wildWinSound) {
            this.system.play(`robinhood`);
        }
        if (multiplierOfBetWon >= this.settings.thresholds.symbolWinSound) {

            if (symbol === CoreSymbolID.LO) {
                this.system.play(`symbolLO`);
            }
            else if (symbol.startsWith(`M`)) {
                this.system.play(`symbol${symbol}`);
            }
            else if (usesWild) {
                this.system.play(`symbolWW`);
            }
        }
    }

    winlineBonusSymbol() {
        this.system.play(`symbolB1`);
    }

    stopAllWinlineSounds() {
        this.system.stop(`robinhood`);
        this.system.stop(`symbolLO`);
        this.system.stop(`symbolWW`);
        for (let index = 1; index <= 5; index++) {//stop M1-5
            this.system.stop(`symbolM${index}`);
        }
    }

    crossbowTimes(num: number) {
        this.system.play(`crossbowTimes${num}`);
    }

    crossbowTimesMultiplier(num: number) {
        this.system.play(`crossbowTimesMultiplier${num}`);
    }

    crossbowSpin() {
        this.system.play(`crossbowSpin`);
    }

    crossbowTargetHit() {
        this.system.play(`crossbowTargetHit`);
    }

    crossbowLand(value: number) {
        this.system.play(`crossbowLand${(++value)}`);
    }

    crossbowWin(moneyWon: number, playerBet: number) {
        if (moneyWon / playerBet >= this.settings.thresholds.crossbowWin) {
            this.system.play(`crossbowWin${this.crossbowBigwin.next()}`);
        }
    }

    crossbowIncrement(targetsIncremented: number[], current: number) {
        let count = 0;
        for (let i = 0; i <= current; i++) {
            if (targetsIncremented[i] >= 0) count++;
        }
        if (count > 0) this.system.play(`crossbowIncrement${count}`);
    }

    stopCrossbowSounds() {
        this.system.stop(`crossbowSpin`);
    }

    consolation(): void {
        this.system.play(`consolation`);
    }

    bonusBell(): void {
        this.system.play(`bonusBell`);
    }

    bonusBellRetrigger(): void {
        this.system.play(`bonusBellRetrigger`);

        Tween.get(this.tweenObjects.bonusBellRetrigger)
            .wait(this.settings.bonusBellRetriggerLength)
            .call(() => this.signals.bonusBellRetriggerComplete.dispatch());
    }

    stopBonusBell() {
        this.system.stop(`bonusBell`);
    }

    skipBonusBellRetrigger() {
        this.stopBonusBell();
        this.system.stop(`bonusBellRetrigger`);
        Tween.removeTweens(this.tweenObjects.bonusBellRetrigger);
        this.signals.bonusBellRetriggerComplete.dispatch();
    }

    bigWinCountUp(): void {
        this.system.play(`bigWinCountUp`);
    }

    bigWinSting(): void {
        this.system.play(`bigWinSting`);
    }

    stopBigWinCountUp(): void {
        this.system.stop(`bigWinCountUp`);
    }

    uiClick(): void {
        this.system.play(`uiClick`);
    }

    reset(): void {

        for (const item of Object.values(this.tweenObjects)) {
            Tween.removeTweens(item);
        }

        this.system.reset();
    }
}